class GeodeCollectionSlider {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-collection-track]');
    this.cards = Array.from(root.querySelectorAll('[data-collection-card]'));
    this.prevBtn = root.querySelector('[data-collection-prev]');
    this.nextBtn = root.querySelector('[data-collection-next]');
    this.paginationCurrent = root.querySelector('.geode-collection-slider__pagination-current');
    this.paginationTotal = root.querySelector('.geode-collection-slider__pagination-total');
    this.highlighted = null;
    this.pointerActive = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.rafHandle = null;
    this.resizeObserver = null;

    if (!this.track || this.cards.length === 0) return;

    this.bind();
    this.updateOverflow();
    this.updateHighlight();
  }

  bind() {
    this.onScroll = this.handleScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerMove = this.handlePointerMove.bind(this);
    this.onPointerUp = this.handlePointerUp.bind(this);

    this.track.addEventListener('pointerdown', this.onPointerDown);

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByDirection(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByDirection(1));
    }

    // Initial state: mark at-start so left fade is hidden
    this.root.classList.add('is-at-start');
  }

  destroy() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointermove', this.onPointerMove);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointerup', this.onPointerUp);
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
  }

  handlePointerDown(event) {
    // Allow clicks without drag when touching links/buttons
    if (event.target.closest('a, button')) return;
    this.pointerActive = true;
    this.root.classList.add('is-dragging');
    this.startX = event.clientX;
    this.startScrollLeft = this.track.scrollLeft;
    this.track.setPointerCapture(event.pointerId);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  handlePointerMove(event) {
    if (!this.pointerActive) return;
    const delta = event.clientX - this.startX;
    this.track.scrollLeft = this.startScrollLeft - delta;
  }

  handlePointerUp(event) {
    if (!this.pointerActive) return;
    this.pointerActive = false;
    this.root.classList.remove('is-dragging');
    this.track.releasePointerCapture(event.pointerId);
    this.track.removeEventListener('pointermove', this.onPointerMove);
    this.track.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointerup', this.onPointerUp);
    this.updateHighlight();
  }

  handleScroll() {
    this.updateEdgeStates();
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = requestAnimationFrame(() => {
      this.updateHighlight();
    });
  }

  updateOverflow() {
    const overflow = this.track.scrollWidth > this.track.clientWidth + 1;
    this.root.classList.toggle('has-overflow', overflow);
    this.updateEdgeStates();
    if (this.paginationTotal) {
      this.paginationTotal.textContent = this.cards.length.toString();
    }
  }

  updateEdgeStates() {
    const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const left = this.track.scrollLeft;
    const atStart = left <= 4;
    const atEnd = left >= max - 4;
    this.root.classList.toggle('is-at-start', atStart);
    this.root.classList.toggle('is-at-end', atEnd);
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }

  updateHighlight() {
    const nextHighlight = this.findHighlight();
    if (!nextHighlight) return;

    if (this.highlighted !== nextHighlight) {
      this.cards.forEach((card) => card.classList.remove('is-highlighted'));
      nextHighlight.classList.add('is-highlighted');
      this.highlighted = nextHighlight;
      this.updatePagination();
    }

    this.cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const viewportLeft = this.track.getBoundingClientRect().left;
      const faded = rect.right < viewportLeft + 24;
      card.classList.toggle('is-fading-left', faded);
    });
  }

  updatePagination() {
    if (!this.paginationCurrent || !this.highlighted) return;
    const index = this.cards.indexOf(this.highlighted);
    this.paginationCurrent.textContent = index >= 0 ? (index + 1).toString() : '1';
  }

  findHighlight() {
    if (this.cards.length === 0) return null;
    
    // Get the track's padding-left - this is where the "visible start" is (aligned with title)
    const trackStyle = getComputedStyle(this.track);
    const paddingLeft = parseFloat(trackStyle.paddingLeft) || 0;
    const trackRect = this.track.getBoundingClientRect();
    const alignmentPoint = trackRect.left + paddingLeft;
    
    let bestCard = null;
    let smallestDistance = Number.MAX_VALUE;

    this.cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      // Card must be at least partially visible
      if (rect.right < trackRect.left || rect.left > trackRect.right) return;
      // Distance from card's left edge to the alignment point
      const distance = Math.abs(rect.left - alignmentPoint);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        bestCard = card;
      }
    });

    return bestCard || this.cards[0];
  }

  scrollByDirection(direction) {
    // Find the current highlighted index
    const currentIndex = this.highlighted ? this.cards.indexOf(this.highlighted) : 0;
    const targetIndex = Math.max(0, Math.min(this.cards.length - 1, currentIndex + direction));
    
    if (targetIndex === currentIndex) return;
    
    // Calculate scroll position based on uniform card widths (before any highlighting)
    // This ensures consistent scrolling regardless of which card is currently expanded
    const gap = this.getGap();
    const normalCardWidth = this.getNormalCardWidth();
    
    // Each card position = index * (normalWidth + gap)
    const scrollOffset = targetIndex * (normalCardWidth + gap);
    this.track.scrollTo({ left: Math.max(0, scrollOffset), behavior: 'smooth' });
  }

  getNormalCardWidth() {
    // Get the base card width from CSS (non-highlighted)
    // Find a card that's not highlighted, or use computed style
    const normalCard = this.cards.find(c => !c.classList.contains('is-highlighted'));
    if (normalCard) {
      return normalCard.offsetWidth;
    }
    // Fallback: read from first card's base flex-basis
    return 240;
  }

  getStepWidth() {
    // Use a non-highlighted card for consistent step size
    const gap = this.getGap();
    const normalCard = this.cards.find(c => !c.classList.contains('is-highlighted')) || this.cards[0];
    const width = normalCard ? normalCard.offsetWidth : 240;
    return width + gap;
  }

  getGap() {
    if (this.cards.length < 2) return 16;
    const first = this.cards[0];
    const second = this.cards[1];
    const rawGap = second.offsetLeft - first.offsetLeft - first.offsetWidth;
    return rawGap > 0 ? rawGap : 16;
  }
}

function initGeodeCollectionSliders(scope = document) {
  const roots = scope.querySelectorAll('[data-collection-slider-root]');
  roots.forEach((root) => {
    if (root.__collectionSlider) return;
    root.__collectionSlider = new GeodeCollectionSlider(root);
  });
}

function destroyGeodeCollectionSliders(scope = document) {
  const roots = scope.querySelectorAll('[data-collection-slider-root]');
  roots.forEach((root) => {
    if (root.__collectionSlider && typeof root.__collectionSlider.destroy === 'function') {
      root.__collectionSlider.destroy();
      delete root.__collectionSlider;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initGeodeCollectionSliders());
} else {
  initGeodeCollectionSliders();
}

document.addEventListener('shopify:section:load', (event) => {
  initGeodeCollectionSliders(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyGeodeCollectionSliders(event.target);
});
